import { sectionRenderer } from '@theme/section-renderer';

const CARD_SELECTOR = '[data-dd-card]';

/**
 * A "View more" pagination button for a product grid rendered by
 * sections/dd-main-collection.liquid. On click, fetches the next page via
 * the Section Rendering API and appends the new cards to the existing grid.
 *
 * A direct load/refresh of e.g. `?page=3` only renders that page's products
 * server-side (Shopify's native pagination just returns that page's slice),
 * so on connect this also fetches and prepends any earlier pages the URL
 * implies were already loaded, to match what "View more" would have built.
 */
class DdViewMore extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('[data-dd-view-more-button]');
    this.grid = this.closest('.dd-main-collection')?.querySelector('[data-dd-grid]') ?? null;
    this.button?.addEventListener('click', this.loadNextPage);
    this.#loadSkippedPreviousPages();
  }

  disconnectedCallback() {
    this.button?.removeEventListener('click', this.loadNextPage);
  }

  get sectionId() {
    const id = this.getAttribute('section-id');
    if (!id) throw new Error('dd-view-more: section-id attribute is required');
    return id;
  }

  async #loadSkippedPreviousPages() {
    const { grid, button } = this;
    const firstCard = grid?.querySelector(`:scope > ${CARD_SELECTOR}`);
    const renderedPage = firstCard ? Number(firstCard.dataset.page) : 1;
    if (!grid || !renderedPage || renderedPage <= 1) return;

    if (button) button.disabled = true;

    try {
      const pages = Array.from({ length: renderedPage - 1 }, (_, i) => i + 1);
      const pageCardLists = await Promise.all(pages.map((page) => this.#fetchPageCards(page, { useCache: true })));

      for (const cards of pageCardLists.reverse()) {
        grid.prepend(...cards);
      }
    } catch (error) {
      // Leave the grid as-is; "View more" going forward still works.
    } finally {
      if (button) button.disabled = false;
    }
  }

  loadNextPage = async () => {
    const { grid, button } = this;
    if (!grid || !button || button.disabled) return;

    const lastPage = Number(grid.dataset.lastPage);
    const cards = grid.querySelectorAll(`:scope > ${CARD_SELECTOR}`);
    const lastCard = cards[cards.length - 1];
    const currentPage = lastCard ? Number(lastCard.dataset.page) : 1;
    const nextPage = currentPage + 1;

    if (lastPage && nextPage > lastPage) {
      this.hidden = true;
      return;
    }

    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    delete button.dataset.error;

    try {
      const newCards = await this.#fetchPageCards(nextPage, { updateHistory: true });
      grid.append(...newCards);

      if (Number(grid.dataset.lastPage) && nextPage >= Number(grid.dataset.lastPage)) {
        this.hidden = true;
      }
    } catch (error) {
      button.dataset.error = 'true';
      button.textContent = "Couldn't load more products. Please try again.";
    } finally {
      button.disabled = false;
      button.removeAttribute('aria-busy');
    }
  };

  /**
   * Fetches a single page's cards via the Section Rendering API.
   * @param {number} pageNumber
   * @param {{ useCache?: boolean, updateHistory?: boolean }} [options]
   * @returns {Promise<NodeListOf<Element>>}
   */
  async #fetchPageCards(pageNumber, { useCache = false, updateHistory = false } = {}) {
    const url = new URL(window.location.href);
    url.searchParams.set('page', String(pageNumber));
    url.hash = '';

    const html = await sectionRenderer.getSectionHTML(this.sectionId, useCache, new URL(url));
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const newGrid = parsed.querySelector('[data-dd-grid]');
    const newCards = newGrid ? newGrid.querySelectorAll(`:scope > ${CARD_SELECTOR}`) : [];

    if (this.grid && newGrid?.dataset.lastPage) {
      this.grid.dataset.lastPage = newGrid.dataset.lastPage;
    }

    if (updateHistory) history.pushState('', '', url.toString());

    return newCards;
  }
}

if (!customElements.get('dd-view-more')) {
  customElements.define('dd-view-more', DdViewMore);
}
